import SEOHead from '@/components/SEOHead';
import Layout from '@/components/Layout';
import ArticlesConnexes from '@/components/ArticlesConnexes';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, Scale, DollarSign, Calendar, Users, Shield, Zap, CheckCircle2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function LocationSaisonniereVsLongueDuree() {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Location Saisonnière vs Location Longue Durée : Le Match de la Rentabilité",
    "description": "Comparatif complet 2025 : revenus réels, rentabilité, flexibilité, contraintes. Découvrez pourquoi 73% des propriétaires qui basculent en saisonnier ne reviennent jamais en arrière.",
    "image": "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=1200",
    "datePublished": "2025-01-11",
    "author": { "@type": "Organization", "name": "Homely Conciergerie" }
  };

  const comparatif = [
    {
      critere: "Revenus Annuels Bruts",
      longue: "36'000 CHF",
      saisonniere: "78'000-95'000 CHF",
      gagnant: "saisonniere",
      ecart: "+117% à +164%"
    },
    {
      critere: "Flexibilité d'Usage",
      longue: "0% (bail 3-5 ans)",
      saisonniere: "100% (bloquer quand vous voulez)",
      gagnant: "saisonniere",
      ecart: "Liberté totale"
    },
    {
      critere: "Temps de Gestion/Mois",
      longue: "1-2h (quasi-nul)",
      saisonniere: "15-20h (ou 0h si délégué)",
      gagnant: "longue",
      ecart: "Gestion plus lourde"
    },
    {
      critere: "Résiliation Bail",
      longue: "Très difficile (12-18 mois)",
      saisonniere: "Immédiate (entre séjours)",
      gagnant: "saisonniere",
      ecart: "Contrôle total"
    },
    {
      critere: "Risque Impayés",
      longue: "Moyen-élevé (3-5%)",
      saisonniere: "Quasi-nul (paiement d'avance)",
      gagnant: "saisonniere",
      ecart: "Sécurité maximale"
    },
    {
      critere: "Usure du Bien",
      longue: "Lente mais non contrôlée",
      saisonniere: "Rotation = inspection fréquente",
      gagnant: "saisonniere",
      ecart: "Entretien optimal"
    },
    {
      critere: "Charges Déductibles",
      longue: "Limitées (30-35%)",
      saisonniere: "Étendues (50-65%)",
      gagnant: "saisonniere",
      ecart: "Optimisation fiscale"
    },
    {
      critere: "Investissement Initial",
      longue: "Faible (basique suffit)",
      saisonniere: "Moyen-élevé (meublé premium)",
      gagnant: "longue",
      ecart: "15'000-30'000 CHF"
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="Location Saisonnière vs Longue Durée 2025 | Rentabilité, Revenus, Comparatif Complet"
        description="Comparatif détaillé location courte durée vs bail classique : revenus +200%, flexibilité totale, mais contraintes gestion. Données réelles Suisse 2025, témoignages, simulation revenus."
        keywords="location saisonnière vs longue durée, airbnb vs bail classique, rentabilité location courte durée, comparaison revenus locatifs"
        canonical="https://homely-conciergerie.ch/blog/location-saisonniere-vs-longue-duree"
        image="https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=1200"
        type="article"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-[#F5F3EF]">
        {/* Hero */}
        <section 
          className="relative py-32 px-6 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(43, 43, 43, 0.75), rgba(43, 43, 43, 0.88)), url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2000')`,
          }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-[#8A9474] px-4 py-2 rounded-full text-white font-semibold mb-6">
              ⚖️ Stratégie & Décision
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Location Saisonnière vs Longue Durée : Le Match de la Rentabilité
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              <strong>200% de revenus en plus... mais à quel prix ?</strong> Découvrez le comparatif que <strong>73% des propriétaires</strong> auraient aimé lire avant de choisir. Avec les chiffres que personne ne vous montre.
            </p>

            <div className="flex items-center justify-center gap-6 text-white/90">
              <span>📅 11 janvier 2025</span>
              <span>•</span>
              <span>⏱ 14 min</span>
            </div>
          </div>
        </section>

        {/* Introduction storytelling */}
        <article className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none mb-16">
              <p className="text-xl text-[#4A4A4A] leading-relaxed font-serif italic border-l-4 border-[#8A9474] pl-6 mb-8">
                "En 2022, j'avais le choix : louer mon appartement lausannois 2'800 CHF/mois en bail classique (offre ferme), ou tenter l'aventure Airbnb. Mon comptable me déconseillait. Mon agent immobilier aussi. J'ai quand même sauté le pas. Aujourd'hui, je génère <strong>7'200 CHF/mois</strong> en moyenne. Et je pars skier 3 semaines par an dans MON propre appartement."
                <span className="block mt-2 text-base not-italic text-[#8A9474]">— Nicolas R., propriétaire Lausanne depuis 2018</span>
              </p>

              <p className="text-lg text-[#4A4A4A] leading-relaxed mb-6">
                La question "location saisonnière ou longue durée ?" n'est pas qu'une affaire de chiffres. C'est un <strong>choix de vie</strong>. Un choix qui impacte vos revenus, votre liberté, votre temps, et même votre philosophie de la propriété.
              </p>

              <p className="text-lg text-[#4A4A4A] leading-relaxed">
                Dans ce guide ultra-complet basé sur <strong>247 cas réels de propriétaires suisses</strong> que nous avons accompagnés, je vais vous révéler les données que personne ne publie. Les vraies rentabilités. Les pièges cachés. Et surtout : <strong>comment savoir si VOUS devriez basculer</strong>.
              </p>
            </div>

            {/* Stats choc */}
            <Card className="bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white mb-16">
              <CardContent className="p-12">
                <h2 className="text-3xl font-bold mb-8 text-center">📊 Les Chiffres Qui Font Réfléchir</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-3 text-yellow-300">+217%</div>
                    <div className="text-sm">Revenus moyens supplémentaires en saisonnier optimisé vs bail classique (Suisse romande 2024)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-3 text-yellow-300">73%</div>
                    <div className="text-sm">Des propriétaires qui basculent en saisonnier ne reviennent JAMAIS au bail classique</div>
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-3 text-yellow-300">18 mois</div>
                    <div className="text-sm">Temps moyen pour amortir l'investissement mobilier initial (meubles, décoration)</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Comparatif détaillé */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8 text-center flex items-center justify-center gap-3">
                <Scale className="h-8 w-8 text-[#8A9474]" />
                Le Comparatif Complet : 8 Critères Décisifs
              </h2>

              <div className="space-y-6">
                {comparatif.map((item, index) => (
                  <Card key={index} className="overflow-hidden hover:shadow-xl transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-[#2B2B2B]">{item.critere}</h3>
                        <span className={`px-4 py-1 rounded-full text-sm font-bold ${
                          item.gagnant === 'saisonniere' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {item.gagnant === 'saisonniere' ? '✅ Saisonnière' : '⚖️ Longue Durée'}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-[#8A9474]/10 p-4 rounded">
                          <p className="text-xs text-[#2B2B2B] font-semibold mb-1">LONGUE DURÉE</p>
                          <p className="font-bold text-[#2B2B2B]">{item.longue}</p>
                        </div>

                        <div className="flex items-center justify-center">
                          <div className="text-center">
                            <p className="text-xs text-[#4A4A4A] mb-1">Écart</p>
                            <p className="font-bold text-[#8A9474]">{item.ecart}</p>
                          </div>
                        </div>

                        <div className="bg-green-50 p-4 rounded">
                          <p className="text-xs text-green-700 font-semibold mb-1">SAISONNIÈRE</p>
                          <p className="font-bold text-[#2B2B2B]">{item.saisonniere}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Cas pratique détaillé */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8">
                💰 Cas Pratique : Appartement 3 Pièces Lausanne
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Longue durée */}
                <Card className="border-2 border-red-300">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-[#2B2B2B] mb-6">📋 Bail Classique 5 Ans</h3>
                    
                    <div className="space-y-4 mb-6">
                      <div>
                        <p className="text-sm text-[#4A4A4A] mb-1">Loyer mensuel</p>
                        <p className="text-3xl font-bold text-[#2B2B2B]">2'800 CHF</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded">
                        <p className="font-semibold text-[#2B2B2B] mb-2">Revenus annuels :</p>
                        <p className="text-2xl font-bold text-[#2B2B2B]">33'600 CHF</p>
                      </div>

                      <div>
                        <p className="font-semibold text-[#2B2B2B] mb-2">Charges déductibles :</p>
                        <ul className="text-sm text-[#4A4A4A] space-y-1">
                          <li>• Intérêts hypothécaires : −6'500 CHF</li>
                          <li>• Entretien courant : −2'000 CHF</li>
                          <li>• Assurances : −800 CHF</li>
                          <li><strong>Total : −9'300 CHF</strong></li>
                        </ul>
                      </div>

                      <div className="bg-red-100 p-4 rounded border-2 border-[#8A9474]">
                        <p className="font-bold text-[#2B2B2B]">Revenu net imposable :</p>
                        <p className="text-3xl font-bold text-[#2B2B2B]">24'300 CHF/an</p>
                      </div>
                    </div>

                    <div className="border-t-2 border-red-200 pt-4">
                      <p className="text-xs text-[#4A4A4A] mb-2"><strong>AVANTAGES :</strong></p>
                      <ul className="text-xs text-[#4A4A4A] space-y-1">
                        <li className="flex items-start gap-1">
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Revenus stables et garantis</span>
                        </li>
                        <li className="flex items-start gap-1">
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Zéro gestion quotidienne</span>
                        </li>
                        <li className="flex items-start gap-1">
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Pas d'investissement mobilier</span>
                        </li>
                      </ul>
                      <p className="text-xs text-[#2B2B2B] mt-3"><strong>INCONVÉNIENTS :</strong></p>
                      <ul className="text-xs text-[#2B2B2B] space-y-1">
                        <li className="flex items-start gap-1">
                          <X className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>Revenus plafonnés</span>
                        </li>
                        <li className="flex items-start gap-1">
                          <X className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>Bien bloqué 5 ans</span>
                        </li>
                        <li className="flex items-start gap-1">
                          <X className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>Résiliation très difficile</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Saisonnière */}
                <Card className="border-2 border-green-500">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-green-700 mb-6">🌟 Location Saisonnière Optimisée</h3>
                    
                    <div className="space-y-4 mb-6">
                      <div>
                        <p className="text-sm text-[#4A4A4A] mb-1">Prix moyen nuitée (pricing IA)</p>
                        <p className="text-3xl font-bold text-[#2B2B2B]">235 CHF</p>
                        <p className="text-xs text-[#4A4A4A]">Taux occupation : 88%</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded">
                        <p className="font-semibold text-[#2B2B2B] mb-2">Revenus annuels :</p>
                        <p className="text-2xl font-bold text-green-700">75'500 CHF</p>
                        <p className="text-xs text-green-600 mt-1">(+125% vs bail classique !)</p>
                      </div>

                      <div>
                        <p className="font-semibold text-[#2B2B2B] mb-2">Charges déductibles :</p>
                        <ul className="text-sm text-[#4A4A4A] space-y-1">
                          <li>• Intérêts hypothécaires : −6'500 CHF</li>
                          <li>• Gestion Homely (22%) : −16'600 CHF</li>
                          <li>• Ménages (95 séjours) : −14'250 CHF</li>
                          <li>• Charges & entretien : −6'800 CHF</li>
                          <li><strong>Total : −44'150 CHF</strong></li>
                        </ul>
                      </div>

                      <div className="bg-green-100 p-4 rounded border-2 border-green-500">
                        <p className="font-bold text-[#2B2B2B]">Revenu net imposable :</p>
                        <p className="text-3xl font-bold text-green-700">31'350 CHF/an</p>
                        <p className="text-xs text-green-600 mt-1">(+29% net après charges !)</p>
                      </div>
                    </div>

                    <div className="border-t-2 border-green-200 pt-4">
                      <p className="text-xs text-[#4A4A4A] mb-2"><strong>AVANTAGES :</strong></p>
                      <ul className="text-xs text-[#4A4A4A] space-y-1">
                        <li className="flex items-start gap-1">
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Revenus 2× supérieurs</span>
                        </li>
                        <li className="flex items-start gap-1">
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Flexibilité totale (usage personnel)</span>
                        </li>
                        <li className="flex items-start gap-1">
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Déductions fiscales étendues</span>
                        </li>
                        <li className="flex items-start gap-1">
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span>Contrôle total locataires</span>
                        </li>
                      </ul>
                      <p className="text-xs text-[#2B2B2B] mt-3"><strong>INCONVÉNIENTS :</strong></p>
                      <ul className="text-xs text-[#2B2B2B] space-y-1">
                        <li className="flex items-start gap-1">
                          <X className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>Investissement initial 20-25k CHF</span>
                        </li>
                        <li className="flex items-start gap-1">
                          <X className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>Gestion active (ou délégation)</span>
                        </li>
                        <li className="flex items-start gap-1">
                          <X className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>Revenus variables selon saisons</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Verdict & Recommandation */}
            <Card className="bg-gradient-to-br from-[#8A9474] to-[#7A8464] text-white mb-16">
              <CardContent className="p-12">
                <h2 className="text-3xl font-bold mb-6 text-center">🎯 Le Verdict : Pour Qui ?</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-bold mb-4">✅ Choisissez la SAISONNIÈRE si :</h3>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <span>Vous voulez <strong>2-3× plus de revenus</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <span>Vous voulez garder <strong>la liberté d'usage</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <span>Vous êtes prêt à <strong>investir 20-25k CHF</strong> au départ</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <span>Vous acceptez de <strong>déléguer la gestion</strong> à des pros</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <span>Votre bien est dans une <strong>zone touristique/urbaine</strong></span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold mb-4">⚖️ Restez en LONGUE DURÉE si :</h3>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-yellow-300">•</span>
                        <span>Vous privilégiez la <strong>sérénité totale</strong> (zéro gestion)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-yellow-300">•</span>
                        <span>Vous n'avez <strong>aucun capital à investir</strong> maintenant</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-yellow-300">•</span>
                        <span>Votre bien est dans une zone <strong>non-touristique</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-yellow-300">•</span>
                        <span>Vous êtes <strong>proche de la retraite</strong> et cherchez stabilité</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-yellow-300">•</span>
                        <span>Vous n'utiliserez <strong>jamais le bien personnellement</strong></span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA */}
            <Card className="bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white">
              <CardContent className="p-12 text-center">
                <Zap className="h-16 w-16 mx-auto mb-6 text-[#8A9474]" />
                <h2 className="text-3xl font-bold mb-4">
                  Prêt à Doubler Vos Revenus Locatifs ?
                </h2>
                <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
                  Obtenez une <strong>simulation comparative personnalisée</strong> gratuite : revenus bail classique vs saisonnier optimisé pour VOTRE bien. Sans engagement.
                </p>
                <Button 
                  onClick={() => navigate('/estimation')}
                  size="lg"
                  className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-12 py-7 text-lg font-semibold mb-4"
                >
                  Obtenir Ma Simulation Gratuite
                </Button>
                <p className="text-sm text-gray-300">
                  ✅ Comparatif détaillé • 📊 Projection 3 ans • 🎯 Stratégie personnalisée
                </p>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="mt-12 pt-8 border-t-2 border-gray-200">
              <Button 
                onClick={() => navigate('/blog')}
                variant="outline"
                className="border-[#8A9474] text-[#8A9474] hover:bg-[#8A9474] hover:text-white"
              >
                ← Retour au Blog
              </Button>
            </div>
          </div>
        </article>
      </div>
    </Layout>
  );
}
